import { useCallback, useEffect, useState } from 'react'
import { type_GM_STATE } from '../../helpers'
import { emitterProps, globalEmitter } from '../helpers/emitter'
import { closeModal, openModal } from '../helpers/useModal'
import { ChevronRightIcon } from '../icons/ChevronRightIcon'
import { PlusOutlineIcon } from '../icons/PlusOutlineIcon'
import { OmneoList, OmneoListItemMeta, type_WISHLIST_DASHBOARD_STATE, typeProduct, typeVariant } from '../types'
import { Modal } from './Modal'
import { getWishlistListCoverImage } from '../helpers/utils'
import { LoaderSpin } from './LoaderSpin'

declare var GM_STATE: type_GM_STATE
declare var wishlistDashboardState: type_WISHLIST_DASHBOARD_STATE

type props = {
  product: typeProduct
  productBiline: string
  variantSelectedLocal: any
  variantSelectedId: number
  templateSuffix: string | null
  propertiesLocal?: any
}

export function WishlistSlideout({ product, productBiline, variantSelectedLocal, variantSelectedId, templateSuffix, propertiesLocal }: props) {
  if (!product) return null

  const [variantSelected, setVariantSelected] = useState<typeVariant | null>(null)

  useEffect(() => {
    const variant = product?.variants.find((variant: any) => variant.id.toString() === variantSelectedId.toString()) || variantSelectedLocal
    if (variant) {
      setVariantSelected(variant)
    }
  }, [variantSelectedId, variantSelectedLocal])

  return (
    <Modal
      id="wishlist-slideout"
      className="modal-wishlist"
      content={
        <RenderContent
          product={product}
          productBiline={productBiline}
          variantSelected={variantSelected}
          templateSuffix={templateSuffix}
          propertiesLocalInfo={propertiesLocal}
        />
      }
      type="slideout"
    />
  )
}

function RenderContent({
  product,
  productBiline,
  variantSelected,
  templateSuffix,
  propertiesLocalInfo
}: {
  product: typeProduct
  productBiline: string
  variantSelected: typeVariant | null
  templateSuffix: string | null
  propertiesLocalInfo: any
}) {
  const [lists, setLists] = useState<OmneoList[]>([])
  const [changeList, setChangeList] = useState(true)
  const [propertiesLocal, setPropertiesLocal] = useState<any>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    globalEmitter.on('product:addToWishlist', (values) => {
      setPropertiesLocal(values.addQueue)
    })
  }, [])

  useEffect(() => {
    if (propertiesLocalInfo) {
      setPropertiesLocal(propertiesLocalInfo)
    }
  }, [propertiesLocalInfo])

  const getMinimumProps = useCallback((): OmneoListItemMeta | null => {
    if (!variantSelected) {
      console.error('Item must have a selected variant to be added to a list', variantSelected)
      return null
    }
    const minimumProps: OmneoListItemMeta = {
      handle: product.handle,
      image: {
        src: getImage() || '',
        alt: product.title
      },
      productBiline,
      productId: product.id,
      variantId: variantSelected.id,
      variantPrice: variantSelected.price,
      variantSku: variantSelected.sku,
      variantOptions: [variantSelected.option1, variantSelected.option2, variantSelected.option3],
      variantAvailable: variantSelected.available,
      templateSuffix,
      properties: propertiesLocal
    }
    return minimumProps
  }, [product, productBiline, variantSelected, templateSuffix, propertiesLocalInfo])

  const fetchLists = async () => {
    setIsLoading(true)
    if (!window.omneoService) return
    const lists = await window.omneoService.getLists()
    setLists(lists)
    setIsLoading(false)
  }

  const getImage = () => {
    if (propertiesLocal.some((property: any) => property.hasOwnProperty('pair'))) {
      if (propertiesLocal.some((property: any) => property.pair === 'true')) {
        return product.featured_image
      } else {
        return variantSelected?.featured_image?.src
      }
    } else {
      return variantSelected?.featured_image?.src || product.images[0]
    }
  }

  const addItem = async (listHandle: string) => {
    if (!listHandle || !product || !variantSelected) {
      globalEmitter.emit('triggerNotification', { message: 'Item could not be added to wishlist', type: 'error' })
      return
    }
    const minimumProps = getMinimumProps()
    if (!minimumProps) {
      globalEmitter.emit('triggerNotification', { message: 'Item could not be added to wishlist', type: 'error' })
      return
    }

    const omneoItem = window.omneoService.getOmneoItem(minimumProps)
    const result = await window.omneoService.addItemToList(listHandle, omneoItem)
    if (!result) {
      globalEmitter.emit('triggerNotification', { message: 'Item could not be added to wishlist', type: 'error' })
      return
    }

    globalEmitter.emit('triggerNotification', { message: 'Item successfully added to wishlist', type: 'success' })
    closeModal('wishlist-slideout')
    globalEmitter.emit(emitterProps.wishlist.add)
    setChangeList(!changeList)
  }

  const openCreateList = async () => {
    const minimumProps = getMinimumProps()
    const modalContext = minimumProps
      ? { item: window.omneoService.getOmneoItem(minimumProps) } //
      : null

    openModal('create-list-slideout', modalContext)
  }

  useEffect(() => {
    globalEmitter.on('createNewList', () => {
      setChangeList(!changeList)
    })
    globalEmitter.on(emitterProps.wishlist.remove, () => {
      setChangeList(!changeList)
    })
  })

  useEffect(() => {
    if (GM_STATE.customer.logged) {
      fetchLists().then()
    }
  }, [changeList])


  return (
    <div className="">
      <h3 className="mb-6 text-base uppercase text-grey-700">{wishlistDashboardState.chooseWishlist}</h3>
      <LoaderSpin show={isLoading && lists.length === 0} />
      {lists.length > 0 &&
        lists.map((list) => {
          const image = getWishlistListCoverImage(list)

          // indicates the src does not have a protocol
          if (image.src.slice(0,5) !== 'https' && image.src.slice(0,2) === '//') {
            image.src = `https:${image.src}`
          }

          // attempt to resize image
          sizeImageCorrectly: try {
            const imageUrl = new URL(image.src)
            const params = imageUrl.searchParams
            if (params.get('width')) break sizeImageCorrectly

            // approx double size required; better than a 4k image
            params.set('width', '160')
            image.src = imageUrl.toString()
          } catch (err) {
            console.warn('Attempted to add width param to this src', { src: image.src, err })
          }

          return (
            <div className="flex items-center w-full mt-6 cursor-pointer" key={list.id} onClick={() => addItem(list.handle)}>
              <div className="relative aspect-[84/102] w-[84px] bg-grey-900">
                <img className="absolute top-0 left-0 object-cover w-full h-full" src={image.src} alt={image.alt} loading="lazy"/>
              </div>
              <div className="flex-1 ml-6">
                <h4 className="m-0 text-sm uppercase">{list.name}</h4>
                <p className="mt-1.5 text-xs uppercase text-grey-400">{list.is_shared ? 'Public' : 'Private'}</p>
              </div>
              <ChevronRightIcon className="w-3 h-3" />
            </div>
          )
        })
      }
      <div className="flex items-center w-full mt-6 cursor-pointer" onClick={openCreateList}>
        <div className="relative aspect-[84/102] object-cover w-[84px] bg-grey-900">
          <span className="absolute -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2">
            <PlusOutlineIcon />
          </span>
        </div>
        <div className="flex-1 ml-6">
          <h4 className="m-0 text-sm uppercase">{wishlistDashboardState.createNewList}</h4>
        </div>
      </div>
    </div>
  )
}
